import React from "react";
import { useState, useEffect } from "react";
import moment from "moment";

import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { toast } from "react-hot-toast";

import TextField from "@mui/material/TextField";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import BadgeUnidade from "../../../../../../shared/components/badgeUnidade/BadgeUnidade";
import SessaoInfoTooltip from "../../../../../planoTratamento/executar/sessaoInfoTooltip/SessaoInfoTooltip";
import "./ModalExecutar.scss";

import StarBorderIcon from "@mui/icons-material/StarBorder";
import DoneIcon from "@mui/icons-material/Done";
import SettingsIcon from "@mui/icons-material/Settings";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useLoading } from "../../../../../../shared/context/LoadingContext";

/* Este ficheiro identifica tudo por `_id`, nunca pelo virtual `id`.
 *
 * O `_id` está presente em todas as formas por onde um plano passa; o `id` é um
 * virtual do mongoose que sobrevive a umas serializações e não a outras — o
 * plano que chega a este ecrã trá-lo, o que o servidor devolve depois de apagar
 * uma sessão não. Depender dele dava um ecrã que funcionava à primeira e
 * falhava à segunda, e comparações entre dois indefinidos que davam verdadeiras
 * para tudo. */

function ModalExecutar({
  plano,
  openModal,
  handleCloseModal,
  sendRequest,
  auth,
  updatePlanosTratamento,
}) {
  const { startLoading, stopLoading } = useLoading();
  const [evolucao, setEvolucao] = useState();
  const [planoTratamento, setPlanoTratamento] = useState();
  const [procedimento, setProcedimento] = useState();
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [step, setStep] = useState("1");
  const [dataInicioExecucao, setDataInicioExecucao] = useState(moment());
  const [dataFimExecucao, setDataFimExecucao] = useState(moment());

  useEffect(() => {
    const sortedProcedimentos = [...plano.procedimentos].sort((a, b) => {
      if (a.procedimento_completo && !b.procedimento_completo) {
        return 1;
      }
      if (!a.procedimento_completo && b.procedimento_completo) {
        return -1;
      }
      return 0;
    });
    const planoFinal = { ...plano, procedimentos: sortedProcedimentos };

    setPlanoTratamento(planoFinal);
  }, [plano]);

  const submitHandler = async () => {
    try {
      startLoading();

      const procedimentosTrabalhados = [];
      let procedimentoEfetuado;

      planoTratamento.procedimentos.forEach((proc) => {
        if (String(proc._id) === String(procedimento._id)) {
          procedimentoEfetuado = proc.tratamento.designacao;
          procedimentosTrabalhados.push({
            ...proc,
            tratamento: proc.tratamento._id,
            procedimento_completo: true,
            data_inicio_execucao: proc.sessoes[0]
              ? proc.sessoes[0].data_inicio_execucao
              : dataInicioExecucao,
            data_fim_execucao: dataFimExecucao,
            sessoes: [
              ...proc.sessoes.map((s) => ({ ...s, medico: s.medico._id })),
              {
                data_inicio_execucao: dataInicioExecucao,
                data_fim_execucao: dataFimExecucao,
                evolucao: evolucao ? evolucao : "Procedimento Executado",
                medico: auth.userId,
              },
            ],
          });
        } else {
          procedimentosTrabalhados.push({
            ...proc,
            tratamento: proc.tratamento._id,
          });
        }
      });

      const formData = new FormData();

      formData.append("utilizador", auth.userId);
      formData.append(
        "procedimentos",
        JSON.stringify(procedimentosTrabalhados),
      );
      formData.append("operacao", "EXECUCAO");
      formData.append("procedimentoId", procedimento._id);
      formData.append("procedimentoNome", procedimentoEfetuado);

      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/planostratamento/${planoTratamento._id}`,
        "PATCH",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      //Se o plano terminar, o orcamento fica a finalizado
      const planoAtualizado = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/planostratamento/${planoTratamento._id}`,
        "GET",
        null,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      setPlanoTratamento(planoAtualizado.planoTratamento);

      toast.success("Procedimento finalizado!");

      updatePlanosTratamento({
        ...planoAtualizado.planoTratamento,
        data_inicio: moment(planoAtualizado.planoTratamento.data_inicio).format(
          "DD-MM-YYYY",
        ),
        data_fim: planoAtualizado.planoTratamento.data_fim
          ? moment(planoAtualizado.planoTratamento.data_fim).format(
              "DD-MM-YYYY",
            )
          : planoAtualizado.planoTratamento.data_fim,
      });

      setEvolucao("");

      setStep("1");
    } catch (err) {
      console.error("err", err);
    } finally {
      setIsSubmiting(false);
      stopLoading();
    }
  };

  const handleGuardar = async () => {
    if (!dataInicioExecucao) {
      toast.error("Introduza a data de inicio de execução");
      return;
    }
    if (!moment.isMoment(dataInicioExecucao) || !dataInicioExecucao.isValid()) {
      toast.error("Introduza a data de inicio de execução corretamente");
      return;
    }
    if (!dataFimExecucao) {
      toast.error("Introduza a data de fim de execução");
      return;
    }
    if (!moment.isMoment(dataFimExecucao) || !dataFimExecucao.isValid()) {
      toast.error("Introduza a data de fim de execução corretamente");
      return;
    }
    if (moment(dataFimExecucao).isBefore(moment(dataInicioExecucao))) {
      toast.error(
        "A data de fim de execução não pode ser antes da data de inicio.",
      );
      return;
    }
    setIsSubmiting(true);
    startLoading();
    try {
      //Ao clicar em guardar, é adicionada uma sessão
      const formData = new FormData();
      formData.append("planoTratamentoId", planoTratamento._id);
      formData.append("data_inicio_execucao", dataInicioExecucao);
      formData.append("data_fim_execucao", dataFimExecucao);
      formData.append(
        "evolucao",
        evolucao ? evolucao : "Procedimento executado.",
      );
      formData.append("medico", auth.userId);
      formData.append("nomeProcedimento", procedimento.tratamento.designacao);

      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/planostratamento/adicionarsessao/${procedimento._id}`,
        "PATCH",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      const planoAtualizado = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/planostratamento/${planoTratamento._id}`,
        "GET",
        null,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      setPlanoTratamento(planoAtualizado.planoTratamento);

      updatePlanosTratamento({
        ...planoAtualizado.planoTratamento,
        data_inicio: moment(planoAtualizado.planoTratamento.data_inicio).format(
          "DD-MM-YYYY",
        ),
        data_fim: planoAtualizado.planoTratamento.data_fim
          ? moment(planoAtualizado.planoTratamento.data_fim).format(
              "DD-MM-YYYY",
            )
          : planoAtualizado.planoTratamento.data_fim,
      });

      toast.success(`Sessão do procedimento efetuada!`);
      setEvolucao("");
      setStep("1");
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmiting(false);
      stopLoading();
    }
  };

  const preSubmitHandler = () => {
    if (!dataInicioExecucao) {
      toast.error("Introduza a data de inicio de execução");
    } else if (
      !moment.isMoment(dataInicioExecucao) ||
      !dataInicioExecucao.isValid()
    ) {
      toast.error("Introduza a data de inicio de execução corretamente");
    } else if (!dataFimExecucao) {
      toast.error("Introduza a data de fim de execução");
    } else if (
      !moment.isMoment(dataFimExecucao) ||
      !dataFimExecucao.isValid()
    ) {
      toast.error("Introduza a data de fim de execução corretamente");
    } else if (moment(dataFimExecucao).isBefore(moment(dataInicioExecucao))) {
      toast.error(
        "A data de fim de execução não pode ser antes da data de inicio.",
      );
    } else {
      setIsSubmiting(true);
      submitHandler();
    }
  };

  const handleChooseProcedimento = (idPro) => {
    setStep("2");

    const procedimentoFiltrado = planoTratamento.procedimentos.filter(
      (p) => String(p._id) === String(idPro),
    )[0];
    setProcedimento(procedimentoFiltrado);
  };

  const handleVoltar = () => {
    setProcedimento(null);
    setEvolucao("");
    setStep("1");
  };

  /* Eliminar uma sessão daqui, como já se fazia no histórico.
     Mesma regra de quem pode: só o médico que a executou. */
  const [sessaoAApagar, setSessaoAApagar] = useState(null);

  const handleApagarSessao = async (sessaoId) => {
    startLoading();
    try {
      const formData = new FormData();
      formData.append("sessaoId", sessaoId);

      const planoRetornado = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/planostratamento/eliminarsessao/${planoTratamento._id}`,
        "PATCH",
        formData,
        { Authorization: "Bearer " + auth.token },
      );

      /* Repõe-se a partir do plano que o SERVIDOR devolveu, e nunca filtrando a
         lista que já está no ecrã.
         Filtrar a lista local parece equivalente e não é: o servidor também
         recalcula o estado do procedimento, o do plano e as duas datas ao
         apagar uma sessão, e uma cópia local desses cálculos fica errada à
         segunda eliminação. Foi exatamente o que aconteceu no histórico. */
      const planoAtualizado = planoRetornado.plano;
      setPlanoTratamento(planoAtualizado);

      const procAtualizado = planoAtualizado.procedimentos.find(
        (p) => String(p._id) === String(procedimento._id),
      );
      setProcedimento(procAtualizado);

      updatePlanosTratamento({
        ...planoAtualizado,
        data_inicio: planoAtualizado.data_inicio
          ? moment(planoAtualizado.data_inicio).format("DD-MM-YYYY")
          : planoAtualizado.data_inicio,
        data_fim: planoAtualizado.data_fim
          ? moment(planoAtualizado.data_fim).format("DD-MM-YYYY")
          : planoAtualizado.data_fim,
      });

      toast.success("Sessão eliminada com sucesso");

      /* Sem sessões, não há execução a continuar a partir daqui — volta-se à
         lista de procedimentos em vez de ficar um ecrã vazio. */
      if (!procAtualizado?.sessoes?.length) {
        setStep("1");
      }
    } catch (err) {
      console.error("err", err);
      toast.error(err.message || "Erro ao eliminar a sessão");
    } finally {
      setSessaoAApagar(null);
      stopLoading();
    }
  };

  const handleCancelarProcedimento = async (idProc) => {
    startLoading();
    try {
      const procedimentosTrabalhados = [];
      let procedimentoEfetuado;

      planoTratamento.procedimentos.forEach((proc) => {
        procedimentoEfetuado = proc.tratamento.designacao;
        if (String(proc._id) === String(idProc)) {
          procedimentosTrabalhados.push({
            ...proc,
            sessoes: proc.sessoes.slice(0, -1),
            tratamento: proc.tratamento._id,
            procedimento_completo: false,
            data_fim_execucao: null,
            data_inicio_execucao: null,
          });
        } else {
          procedimentosTrabalhados.push({
            ...proc,
            tratamento: proc.tratamento._id,
          });
        }
      });

      const formData = new FormData();
      formData.append("operacao", "CANCELAMENTO");
      formData.append("procedimentoId", idProc);
      formData.append("procedimentoNome", procedimentoEfetuado);

      formData.append(
        "procedimentos",
        JSON.stringify(procedimentosTrabalhados),
      );
      formData.append("utilizador", auth.userId);

      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/planostratamento/${planoTratamento._id}`,
        "PATCH",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      // Buscar plano atualizado do servidor
      const planoAtualizado = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/planostratamento/${planoTratamento._id}`,
        "GET",
        null,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      // Atualizar state local
      setPlanoTratamento(planoAtualizado.planoTratamento);

      // Atualizar componente pai
      updatePlanosTratamento({
        ...planoAtualizado.planoTratamento,
        data_inicio: moment(planoAtualizado.planoTratamento.data_inicio).format(
          "DD-MM-YYYY",
        ),
        data_fim: planoAtualizado.planoTratamento.data_fim
          ? moment(planoAtualizado.planoTratamento.data_fim).format(
              "DD-MM-YYYY",
            )
          : planoAtualizado.planoTratamento.data_fim,
      });

      toast.success("Execução do procedimento cancelado.");
    } catch (err) {
      console.error("err", err);
      toast.error("Erro ao cancelar procedimento");
    } finally {
      setIsSubmiting(false);
      stopLoading();
    }
  };

  return (
    <Modal
      open={openModal}
      onClose={handleCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      {/* Verificar se nos dados da modal conter avaliacao. Se sim, estamos lidando com dados do orcamento */}
      <Box
        className="fluxo-atendimento__modal__box"
        id="modal-executar-procedimentos"
      >
        {step === "1" && (
          <div className="executarProcedimento__step1 caixa">
            <span className="executarProcedimento__step1__tabela-heading executarProcedimento__step1__tabela-heading--1st">
              Tratamento
            </span>
            <span className="executarProcedimento__step1__tabela-heading">
              Dentes
            </span>
            <span className="executarProcedimento__step1__tabela-heading">
              Faces
            </span>
            <span className="executarProcedimento__step1__tabela-heading">
              Sessões
            </span>
            <span className="executarProcedimento__step1__tabela-heading">
              Estado
            </span>
            <span className="executarProcedimento__step1__tabela-heading executarProcedimento__step1__tabela-heading--last">
              Ação
            </span>
            {planoTratamento &&
              planoTratamento.procedimentos.map(
                (p, index) =>
                  !p.procedimento_completo && (
                    <React.Fragment key={index}>
                      <span className="executarProcedimento__step1__lineItem executarProcedimento__step1__lineItem--1st">
                        {p.tratamento.designacao}
                      </span>
                      <span className="executarProcedimento__step1__lineItem">
                        {p.dente}
                      </span>
                      <span className="executarProcedimento__step1__lineItem">
                        {p.faces.join(" ,")}
                      </span>

                      {/* O mesmo detalhe das sessões que a dashboard já dava:
                          quem, quando e em que unidade cada uma foi feita.
                          Aqui via-se só o número, e quem estava na ficha do
                          paciente tinha de ir à dashboard para saber o resto. */}
                      {p.sessoes.length === 0 ? (
                        <span className="executarProcedimento__step1__lineItem">
                          {p.sessoes.length}
                        </span>
                      ) : (
                        <SessaoInfoTooltip procedimento={p} />
                      )}
                      <div className="executarProcedimento__step1__procedimento__container executarProcedimento__step1__lineItem">
                        <span
                          className={`
                        executarProcedimento__step1__procedimento
                        ${
                          p.sessoes.length > 0
                            ? "executarProcedimento__step1__procedimento-emProgresso"
                            : "executarProcedimento__step1__procedimento-novo"
                        }
                      `}
                        >
                          {p.sessoes.length > 0 ? (
                            <>
                              <SettingsIcon className="executarProcedimento__step1__procedimento__icon" />{" "}
                              Em Progresso
                            </>
                          ) : (
                            <>
                              <StarBorderIcon className="executarProcedimento__step1__procedimento__icon" />{" "}
                              Novo
                            </>
                          )}
                        </span>
                      </div>

                      <div className="executarProcedimento__step1__lineItem executarProcedimento__step1__btnContainer">
                        <span
                          className="executarProcedimento__step1__btn-executar executarButton"
                          onClick={handleChooseProcedimento.bind(null, p._id)}
                        >
                          Executar
                        </span>
                      </div>
                    </React.Fragment>
                  ),
              )}
            <div className="linhaCinzenta">Finalizados</div>
            {planoTratamento &&
              planoTratamento.procedimentos.map(
                (p, index) =>
                  p.procedimento_completo && (
                    <React.Fragment key={index}>
                      <span className="executarProcedimento__step1__lineItem executarProcedimento__step1__lineItem--1st">
                        {p.tratamento.designacao}
                      </span>
                      <span className="executarProcedimento__step1__lineItem">
                        {p.dente}
                      </span>
                      <span className="executarProcedimento__step1__lineItem">
                        {p.faces.join(" ,")}
                      </span>

                      {/* O mesmo detalhe das sessões que a dashboard já dava:
                          quem, quando e em que unidade cada uma foi feita.
                          Aqui via-se só o número, e quem estava na ficha do
                          paciente tinha de ir à dashboard para saber o resto. */}
                      {p.sessoes.length === 0 ? (
                        <span className="executarProcedimento__step1__lineItem">
                          {p.sessoes.length}
                        </span>
                      ) : (
                        <SessaoInfoTooltip procedimento={p} />
                      )}
                      <div className="executarProcedimento__step1__procedimento__container executarProcedimento__step1__lineItem">
                        <span
                          className="
                        executarProcedimento__step1__procedimento executarProcedimento__step1__procedimento-completo"
                        >
                          <DoneIcon className="executarProcedimento__step1__procedimento__icon" />{" "}
                          Completo
                        </span>
                      </div>

                      <div className="executarProcedimento__step1__lineItem executarProcedimento__step1__btnContainer">
                        {p.sessoes[p.sessoes.length - 1].medico._id ===
                        auth.userId ? (
                          <span
                            className="executarProcedimento__step1__btn-executar cancelarButton"
                            onClick={handleCancelarProcedimento.bind(
                              null,
                              p._id,
                            )}
                          >
                            Cancelar
                          </span>
                        ) : (
                          <span></span>
                        )}
                      </div>
                    </React.Fragment>
                  ),
              )}
          </div>
        )}

        {step === "2" && (
          <div className="executarProcedimento__step2">
            <div className="modal-executar-procedimentos__datetimeContainer">
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  className="planos-tratamento__modal__datePicker planos-tratamento__modal__datePicker--1"
                  label="Data Inicio"
                  inputFormat="DD-MM-YYYY HH:mm"
                  value={dataInicioExecucao}
                  onChange={(value) => setDataInicioExecucao(value)}
                  renderInput={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  className="planos-tratamento__modal__datePicker planos-tratamento__modal__datePicker--2"
                  label="Data Fim"
                  inputFormat="DD-MM-YYYY HH:mm"
                  value={dataFimExecucao}
                  onChange={(value) => setDataFimExecucao(value)}
                  renderInput={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>
            </div>
            {procedimento && (
              <div className="bottom-orcamento caixa">
                <div className="executarProcedimento__container">
                  <span className="executarProcedimento__container__item--header">
                    Tratamento
                  </span>
                  <span className="executarProcedimento__container__item--header">
                    Dentes/Região
                  </span>

                  <span className="executarProcedimento__container__item--header">
                    Faces
                  </span>

                  <span className="executarProcedimento__container__item">
                    {procedimento.tratamento.designacao}
                  </span>
                  <span className="executarProcedimento__container__item">
                    {procedimento.dente}
                  </span>

                  <span className="executarProcedimento__container__item">
                    {procedimento.faces.join(", ")}
                  </span>

                  <textarea
                    className="executarProcedimento__container__textarea"
                    cols="30"
                    rows="3"
                    value={evolucao}
                    placeholder="Escreva aqui a evolução do procedimento."
                    onChange={(e) => {
                      e.target.value
                        ? setEvolucao(e.target.value)
                        : setEvolucao("");
                    }}
                  />

                  <div className="listaEvolucoesContainer">
                    {procedimento.sessoes.map((sessao, index) => (
                      <div className="evolucoesPassadasContainer" key={index}>
                        <span className="modalEvolucao__container__item--left__evolucao">
                          {sessao.evolucao}
                        </span>
                        <span className="modalEvolucao__container__item--left__otherinfo">
                          {moment(sessao.data_inicio_execucao).format(
                            "DD-MM-YYYY",
                          )}
                          ,{" "}
                          {moment(sessao.data_inicio_execucao).format("HH:mm")}{" "}
                          às {moment(sessao.data_fim_execucao).format("HH:mm")}
                          {" - "}
                          {sessao.medico.genero === "masculino"
                            ? "Dr. "
                            : "Dra. "}
                          {sessao.medico.name}
                          {/* Onde a sessão foi executada. Quem está a executar
                              a seguinte precisa de saber onde as anteriores
                              foram feitas — um procedimento pode ter sessões em
                              unidades diferentes. */}
                          <BadgeUnidade
                            estabelecimento={sessao.estabelecimento}
                            prefixo="Executada em"
                          />
                        </span>

                        {/* Eliminar, como no histórico. Só a quem executou a
                            sessão: um dentista não apaga o registo do trabalho
                            de outro. */}
                        {sessao.medico?._id === auth.userId &&
                          (sessaoAApagar === sessao._id ? (
                            <span className="evolucoesPassadasContainer__btns">
                              <Tooltip title="Confirmar eliminação">
                                <IconButton
                                  onClick={() => handleApagarSessao(sessao._id)}
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancelar">
                                <IconButton
                                  onClick={() => setSessaoAApagar(null)}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </span>
                          ) : (
                            <span className="evolucoesPassadasContainer__btns">
                              <Tooltip title="Eliminar sessão">
                                <IconButton
                                  onClick={() => setSessaoAApagar(sessao._id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </span>
                          ))}
                      </div>
                    ))}
                  </div>
                </div>

                {step === "2" && (
                  <div className="botoes">
                    <button className="cancel-btn" onClick={handleVoltar}>
                      Voltar
                    </button>

                    <button
                      className="blue-button"
                      onClick={handleGuardar}
                      disabled={isSubmiting}
                    >
                      {isSubmiting ? "Guardando" : "Guardar"}
                    </button>

                    <button
                      disabled={isSubmiting}
                      className="blue-button"
                      onClick={preSubmitHandler}
                    >
                      {isSubmiting ? "Finalizando" : "Finalizar"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Box>
    </Modal>
  );
}

export default ModalExecutar;
